import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import useProductCardOptions from 'data-hooks/useProductCardOptions';
import useProductBasicDetails from 'data-hooks/useProductBasicDetails';
import { findAvailabilityAndVisibility } from 'data-hooks/utils';
import isServer from 'commons/isServer';
import { STOCK_VALUE_STATES, product_item_availability } from 'commons/constants';
const isOptionValueInOptions = (options, forMaterial, forMaterialType) => {
  const materialOption = options.find(optVal => optVal.name === forMaterialType);
  if (!materialOption || !materialOption.values.some(opt => opt.value === forMaterial)) {
    return false;
  }
  return true;
};

/**
 *  Used by product cards to determine if product is out of stock (to display OOS label)
 *  or discontinued (to remove from display) based on stock response.
 *  Includes both EFG internal and FFG.
 *
 *  Returns object of shape
 *  {
 *    isProductOOS,
 *    isProductDiscontinued,
 *    isAdditionalVisProductAvailable,
 *    isMarkedOOSOrDiscontinued,
 *    isEFGInternalOOS
 *  }
 */

const useProductAvailabilityByStock = (id, slug, t0) => {
  const $ = _c(83);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hookOptions = t1;
  const {
    isAddlVisProductSku,
    forMaterial,
    forMaterialType,
    layout,
    localQSStock,
    productSku,
    delayLoad: t2,
    stockDataFromParentLoading: t3,
    allowDiscontinuedRemoval: t4,
    hasStockData: t5
  } = hookOptions;
  const delayLoad = t2 === undefined ? false : t2;
  const stockDataFromParentLoading = t3 === undefined ? false : t3;
  const allowDiscontinuedRemoval = t4 === undefined ? false : t4;
  const hasStockData = t5 === undefined ? true : t5;
  let t6;
  if ($[2] !== delayLoad) {
    t6 = isServer() || delayLoad;
    $[2] = delayLoad;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== productSku || $[5] !== t6) {
    t7 = {
      skipPDPStateHook: true,
      productSku,
      delayLoad: t6
    };
    $[4] = productSku;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const {
    data: basicDetailsData,
    loading: basicDetailsLoading
  } = useProductBasicDetails(id, null, t7);
  let t8;
  if ($[7] !== hasStockData || $[8] !== localQSStock || $[9] !== stockDataFromParentLoading) {
    t8 = {
      hasStockData,
      localQSStock,
      stockDataFromParentLoading
    };
    $[7] = hasStockData;
    $[8] = localQSStock;
    $[9] = stockDataFromParentLoading;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== delayLoad) {
    t9 = isServer() || delayLoad;
    $[11] = delayLoad;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== productSku || $[14] !== t8 || $[15] !== t9) {
    t10 = {
      skipLocalStockHook: false,
      localStockHookOptions: t8,
      productSku,
      delayLoad: t9
    };
    $[13] = productSku;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const {
    loading,
    data: optionsData
  } = useProductCardOptions(id, slug, t10);
  const [isAdditionalVisProductAvailable, setIsAdditionalVisProductAvailable] = useState(true);
  const [stockState, setStockState] = useState(STOCK_VALUE_STATES.LOADING);
  const [isProductOOS, setIsProductOOS] = useState(false);
  const [isProductDiscontinued, setIsProductDiscontinued] = useState(false);
  const [isMarkedOOSOrDiscontinued, setIsMarkedOOSOrDiscontinued] = useState(false);
  const [isEFGInternalOOS, setIsEFGInternalOOS] = useState(false);
  let t11;
  if ($[17] !== basicDetailsData || $[18] !== forMaterial || $[19] !== forMaterialType || $[20] !== isAddlVisProductSku || $[21] !== layout || $[22] !== optionsData) {
    t11 = () => {
      if (!optionsData?.options || !forMaterial || !forMaterialType || !isAddlVisProductSku || layout !== "favorite" || !basicDetailsData || basicDetailsData?.efg_flag && !basicDetailsData?.is_external) {
        return;
      }
      if (!optionsData.options?.length && forMaterial && forMaterialType) {
        setIsAdditionalVisProductAvailable(false);
        return;
      }
      setIsAdditionalVisProductAvailable(isOptionValueInOptions(optionsData.options, forMaterial, forMaterialType));
    };
    $[17] = basicDetailsData;
    $[18] = forMaterial;
    $[19] = forMaterialType;
    $[20] = isAddlVisProductSku;
    $[21] = layout;
    $[22] = optionsData;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = optionsData?.options;
  let t13;
  if ($[24] !== basicDetailsData || $[25] !== forMaterial || $[26] !== forMaterialType || $[27] !== isAddlVisProductSku || $[28] !== layout || $[29] !== t12) {
    t13 = [basicDetailsData, forMaterial, forMaterialType, isAddlVisProductSku, layout, t12];
    $[24] = basicDetailsData;
    $[25] = forMaterial;
    $[26] = forMaterialType;
    $[27] = isAddlVisProductSku;
    $[28] = layout;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  useEffect(t11, t13);
  let t14;
  if ($[31] !== allowDiscontinuedRemoval || $[32] !== basicDetailsData || $[33] !== basicDetailsLoading || $[34] !== forMaterial || $[35] !== forMaterialType || $[36] !== isAddlVisProductSku || $[37] !== layout || $[38] !== optionsData || $[39] !== stockState) {
    t14 = () => {
      if (!basicDetailsData || basicDetailsLoading || !optionsData?.stock || !!optionsData?.stock?.loading) {
        return;
      }
      const {
        stockOptions: t15,
        selectedOptionsStockQty: t16
      } = optionsData.stock || {};
      const stockOptions = t15 === undefined ? [] : t15;
      const selectedOptionsStockQty = t16 === undefined ? 0 : t16;
      const isEFGExternal = basicDetailsData.efg_flag && basicDetailsData.is_external;
      let isOutOfStock = false;
      const {
        availability
      } = findAvailabilityAndVisibility(basicDetailsData.attributes);
      if (`${availability}` === `${product_item_availability.out_of_stock}` || `${availability}` === `${product_item_availability.disabled}`) {
        isOutOfStock = true;
      }
      if (forMaterial && forMaterialType && isAddlVisProductSku && layout === "favorite") {
        const isVisible = isOptionValueInOptions(stockOptions, forMaterial, forMaterialType);
        setIsAdditionalVisProductAvailable(isVisible);
        return;
      }
      if (!isOutOfStock && !basicDetailsData.discontinued) {
        return;
      }
      setIsMarkedOOSOrDiscontinued(true);
      if (isEFGExternal) {
        if (basicDetailsData.discontinued) {
          setStockState(STOCK_VALUE_STATES.DISCONTINUED);
          setIsProductDiscontinued(true);
          return;
        }
        if (isOutOfStock) {
          setStockState(STOCK_VALUE_STATES.OOS);
          setIsProductOOS(true);
        }
        return;
      }
      if (!stockOptions.length && !selectedOptionsStockQty && !isAddlVisProductSku && (layout === "category" || allowDiscontinuedRemoval)) {
        if (basicDetailsData.discontinued) {
          setStockState(STOCK_VALUE_STATES.DISCONTINUED);
          setIsProductDiscontinued(true);
          return;
        }
        if (isOutOfStock) {
          setStockState(STOCK_VALUE_STATES.OOS);
          setIsProductOOS(true);
        }
        return;
      }
      if (stockState === STOCK_VALUE_STATES.LOADING && (!!stockOptions.length || !!selectedOptionsStockQty)) {
        if (basicDetailsData.discontinued) {
          setStockState(STOCK_VALUE_STATES.INSTOCK);
          setIsProductDiscontinued(false);
        }
        if (isOutOfStock) {
          setIsProductOOS(false);
        }
      }
    };
    $[31] = allowDiscontinuedRemoval;
    $[32] = basicDetailsData;
    $[33] = basicDetailsLoading;
    $[34] = forMaterial;
    $[35] = forMaterialType;
    $[36] = isAddlVisProductSku;
    $[37] = layout;
    $[38] = optionsData;
    $[39] = stockState;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const t15 = optionsData?.stock;
  let t16;
  if ($[41] !== allowDiscontinuedRemoval || $[42] !== basicDetailsData || $[43] !== basicDetailsLoading || $[44] !== forMaterial || $[45] !== forMaterialType || $[46] !== isAddlVisProductSku || $[47] !== layout || $[48] !== stockState || $[49] !== t15) {
    t16 = [allowDiscontinuedRemoval, basicDetailsData, basicDetailsLoading, forMaterial, forMaterialType, isAddlVisProductSku, layout, t15, stockState];
    $[41] = allowDiscontinuedRemoval;
    $[42] = basicDetailsData;
    $[43] = basicDetailsLoading;
    $[44] = forMaterial;
    $[45] = forMaterialType;
    $[46] = isAddlVisProductSku;
    $[47] = layout;
    $[48] = stockState;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  useEffect(t14, t16);
  let t17;
  if ($[51] !== allowDiscontinuedRemoval || $[52] !== basicDetailsData || $[53] !== basicDetailsLoading || $[54] !== forMaterial || $[55] !== forMaterialType || $[56] !== isAddlVisProductSku || $[57] !== isEFGInternalOOS || $[58] !== isMarkedOOSOrDiscontinued || $[59] !== layout || $[60] !== optionsData || $[61] !== stockState) {
    t17 = () => {
      if (!basicDetailsData || basicDetailsLoading || !optionsData?.stock || !!optionsData?.stock?.loading || !basicDetailsData.efg_flag || basicDetailsData?.is_external || isMarkedOOSOrDiscontinued || forMaterial || forMaterialType || isAddlVisProductSku) {
        return;
      }
      const {
        stockOptions: t18,
        selectedOptionsStockQty: t19
      } = optionsData.stock || {};
      const stockOptions_0 = t18 === undefined ? [] : t18;
      const selectedOptionsStockQty_0 = t19 === undefined ? 0 : t19;
      if (!stockOptions_0.length && !selectedOptionsStockQty_0 && (layout === "category" || allowDiscontinuedRemoval) && !isEFGInternalOOS) {
        setStockState(STOCK_VALUE_STATES.OOS);
        setIsEFGInternalOOS(true);
        return;
      }
      if (stockOptions_0.length || selectedOptionsStockQty_0 && stockState === STOCK_VALUE_STATES.LOADING && isEFGInternalOOS) {
        setStockState(STOCK_VALUE_STATES.INSTOCK);
        setIsEFGInternalOOS(false);
      }
    };
    $[51] = allowDiscontinuedRemoval;
    $[52] = basicDetailsData;
    $[53] = basicDetailsLoading;
    $[54] = forMaterial;
    $[55] = forMaterialType;
    $[56] = isAddlVisProductSku;
    $[57] = isEFGInternalOOS;
    $[58] = isMarkedOOSOrDiscontinued;
    $[59] = layout;
    $[60] = optionsData;
    $[61] = stockState;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  const t18 = optionsData?.stock;
  let t19;
  if ($[63] !== allowDiscontinuedRemoval || $[64] !== basicDetailsData || $[65] !== basicDetailsLoading || $[66] !== forMaterial || $[67] !== forMaterialType || $[68] !== isAddlVisProductSku || $[69] !== isEFGInternalOOS || $[70] !== isMarkedOOSOrDiscontinued || $[71] !== layout || $[72] !== stockState || $[73] !== t18) {
    t19 = [allowDiscontinuedRemoval, basicDetailsData, basicDetailsLoading, forMaterial, forMaterialType, isAddlVisProductSku, isEFGInternalOOS, isMarkedOOSOrDiscontinued, layout, t18, stockState];
    $[63] = allowDiscontinuedRemoval;
    $[64] = basicDetailsData;
    $[65] = basicDetailsLoading;
    $[66] = forMaterial;
    $[67] = forMaterialType;
    $[68] = isAddlVisProductSku;
    $[69] = isEFGInternalOOS;
    $[70] = isMarkedOOSOrDiscontinued;
    $[71] = layout;
    $[72] = stockState;
    $[73] = t18;
    $[74] = t19;
  } else {
    t19 = $[74];
  }
  useEffect(t17, t19);
  const t20 = !!(basicDetailsData?.efg_flag && !basicDetailsData?.is_external);
  let t21;
  if ($[75] !== isAdditionalVisProductAvailable || $[76] !== isEFGInternalOOS || $[77] !== isMarkedOOSOrDiscontinued || $[78] !== isProductDiscontinued || $[79] !== isProductOOS || $[80] !== loading || $[81] !== t20) {
    t21 = {
      loading,
      isAdditionalVisProductAvailable,
      isProductOOS,
      isProductDiscontinued,
      isMarkedOOSOrDiscontinued,
      isEFGInternalOOS,
      isEFGInternal: t20
    };
    $[75] = isAdditionalVisProductAvailable;
    $[76] = isEFGInternalOOS;
    $[77] = isMarkedOOSOrDiscontinued;
    $[78] = isProductDiscontinued;
    $[79] = isProductOOS;
    $[80] = loading;
    $[81] = t20;
    $[82] = t21;
  } else {
    t21 = $[82];
  }
  return t21;
};
export default useProductAvailabilityByStock;